import type { RedoCartItem } from "@redotech/merchant-sdk/rpc/schema/redo-supplies/redo-cart";
import { minorUnitsToAmount } from "@redotech/money/iso4217/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoDrawer } from "@redotech/redo-web/arbiter-components/modal/redo-drawer";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import * as styles from "./cart-drawer.module.css";
import { CartAttributesTooltipContent } from "./cart-item-attributes";
import {
  type RedoSuppliesCartContextValue,
  useRedoSuppliesCart,
} from "./redo-supplies-cart-context";

function formatPrice(minorUnits: number, currency: string): string {
  const amount = minorUnitsToAmount(minorUnits, currency);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(amount);
}

export const CartDrawer = memo(function CartDrawer() {
  const { cart, isCartOpen, setCartOpen, refreshCart, upsertItem } =
    useRedoSuppliesCart();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const navigate = useNavigate();
  const [cartLocked, setCartLocked] = useState(false);
  const team = useContext(TeamContext);

  const startCheckout = useCallback(async () => {
    try {
      const { checkoutId } = await rpcClient.startSuppliesCheckout({});
      setCartOpen(false);
      navigate(`shop/checkout/${checkoutId}`);
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : "Failed to start checkout. Please try again.",
        { variant: "error" },
      );
      refreshCart();
    }
  }, [rpcClient, setCartOpen, navigate, refreshCart]);

  const [checkoutState, triggerCheckout] = useTriggerLoad(async () => {
    await startCheckout();
    return null;
  });

  const handleCheckout = useCallback(() => {
    triggerCheckout();
  }, [triggerCheckout]);

  const lockingUpsertItem: typeof upsertItem = useCallback(
    async (productId, productFamilyId, quantityDelta, productInfo) => {
      setCartLocked(true);
      try {
        await upsertItem(
          productId,
          productFamilyId,
          quantityDelta,
          productInfo,
        );
      } catch {
        toast("Failed to update cart. Please try again.", { variant: "error" });
        refreshCart();
      } finally {
        setCartLocked(false);
      }
    },
    [upsertItem, refreshCart],
  );

  const estimatedTotal = useMemo(() => {
    if (!cart?.items.length) {
      return null;
    }
    let total = 0;
    let currency: string | null = null;
    for (const item of cart.items) {
      total += item.price * item.quantity;
      currency = item.currency;
    }
    if (currency == null) {
      return null;
    }
    return formatPrice(total, currency);
  }, [cart]);

  const handleClose = useCallback(() => setCartOpen(false), [setCartOpen]);

  const paperProps = useMemo(() => ({ className: styles.drawerPaper }), []);

  const newOmsEnabled = !!team?.settings?.outboundLabels?.newOmsEnabled;
  const checkoutDisabled =
    cartLocked || (newOmsEnabled && !cart?.destinationLocationId);
  const checkoutText =
    newOmsEnabled && !cart?.destinationLocationId
      ? "Select a warehouse to checkout"
      : "Proceed to checkout";

  return (
    <RedoDrawer
      onModalCloseRequested={handleClose}
      open={isCartOpen}
      PaperProps={paperProps}
    >
      <Flex dir="column" gap="none" grow={1} overflow="hidden">
        <Flex
          align="center"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          justify="space-between"
          px="3xl"
          py="xl"
        >
          <Text fontSize="lg" fontWeight="semibold">
            Cart
          </Text>
          <RedoButton
            aria-label="Close"
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={handleClose}
            size="sm"
          />
        </Flex>
        <Flex dir="column" grow={1} overflow="auto" p="3xl">
          {!cart?.items.length ? (
            <Flex align="center" grow={1} justify="center">
              <Text textColor="tertiary">Your cart is empty.</Text>
            </Flex>
          ) : (
            <Flex dir="column" gap="lg">
              {cart.items.map((item) => (
                <CartItem
                  disabled={cartLocked}
                  item={item}
                  key={item.id}
                  upsertItem={lockingUpsertItem}
                />
              ))}
            </Flex>
          )}
        </Flex>
        {cart?.items.length ? (
          <Flex
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="column"
            gap="lg"
            px="3xl"
            py="2xl"
          >
            <Flex justify="space-between" w="full">
              <Text fontSize="md" fontWeight="semibold">
                Subtotal
              </Text>
              <Text fontSize="md" fontWeight="semibold">
                {estimatedTotal ?? "—"}
              </Text>
            </Flex>
            <RedoButton
              disabled={checkoutDisabled}
              hierarchy="primary"
              onClick={handleCheckout}
              pending={checkoutState.pending}
              size="lg"
              text={checkoutText}
            />
          </Flex>
        ) : null}
      </Flex>
    </RedoDrawer>
  );
});

const CartItem = memo(function CartItem({
  disabled,
  item,
  upsertItem,
}: {
  disabled: boolean;
  item: RedoCartItem;
  upsertItem: RedoSuppliesCartContextValue["upsertItem"];
}) {
  const productInfo = useMemo(
    () => ({
      title: item.title,
      price: item.price,
      currency: item.currency,
      imageUrl: item.imageUrl,
      options: item.options,
    }),
    [item.title, item.price, item.currency, item.imageUrl, item.options],
  );

  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFlushedQuantity = useRef(item.quantity);

  useEffect(() => {
    if (!debounceTimer.current) {
      setLocalQuantity(item.quantity);
      lastFlushedQuantity.current = item.quantity;
    }
  }, [item.quantity]);

  const flushQuantityUpdate = useCallback(
    async (newQuantity: number) => {
      const delta = newQuantity - lastFlushedQuantity.current;
      lastFlushedQuantity.current = newQuantity;
      if (delta === 0) {
        return;
      }
      await upsertItem(
        item.productId,
        item.productFamilyId,
        delta,
        productInfo,
      );
    },
    [item.productId, item.productFamilyId, upsertItem, productInfo],
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      setLocalQuantity(newQuantity);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        void flushQuantityUpdate(newQuantity);
      }, 300);
    },
    [flushQuantityUpdate],
  );

  const handleRemove = useCallback(async () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    await upsertItem(
      item.productId,
      item.productFamilyId,
      -lastFlushedQuantity.current,
      productInfo,
    );
  }, [item.productId, item.productFamilyId, upsertItem, productInfo]);

  const lineTotal = formatPrice(item.price * item.quantity, item.currency);

  const showOptions =
    item.options.length > 0 &&
    !(
      item.options.length === 1 &&
      item.options[0]?.value.toLowerCase().includes("default title")
    );

  return (
    <Flex align="center" className={styles.itemCard} gap="lg" p="xl">
      {item.imageUrl && (
        <img
          alt={item.title}
          className={styles.itemImage}
          src={item.imageUrl}
        />
      )}
      <Flex dir="column" gap="xs" grow={1} overflow="hidden">
        <Flex align="center" gap="md" justify="space-between">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {item.title}
          </Text>
          <Text fontSize="sm" fontWeight="semibold">
            {lineTotal}
          </Text>
        </Flex>
        {showOptions && (
          <Text fontSize="xs" textColor="tertiary">
            {item.options.map((o) => `${o.name}: ${o.value}`).join(", ")}
          </Text>
        )}
        {item.attributes.some((a) => a.key === "reorderThreshold") && (
          <Tooltip
            title={
              <CartAttributesTooltipContent attributes={item.attributes} />
            }
          >
            <span>
              <RedoBadge color="orange" size="xs" text="Inventory-triggered" />
            </span>
          </Tooltip>
        )}
        <Flex align="center" gap="md" justify="space-between">
          <RedoIncrementDecrement
            disabled={disabled}
            key={item.quantity}
            max={item.maxQuantity ?? 20_000}
            min={1}
            setValue={handleQuantityChange}
            size="small"
            value={localQuantity}
          />
          <RedoButton
            disabled={disabled}
            hierarchy="link_underline"
            onClick={handleRemove}
            size="sm"
            text="Remove"
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
